class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
      const container = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : this.querySelector('.drawer__inner');
      if (container) trapFocus(container, container.querySelector('.drawer__close') || container);
    });

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, options = {}) {
    const scrollTop = this.querySelector('.av-cart-scroll')?.scrollTop || 0;
    const focusedId = this.contains(document.activeElement) ? document.activeElement.id : '';
    this.productId = parsedState.id;
    const drawerHtml = parsedState.sections?.['cart-drawer'];
    if (!drawerHtml) throw new Error('Missing cart section');
    const source = new DOMParser().parseFromString(drawerHtml, 'text/html');
    const sourceDrawer = source.querySelector('cart-drawer');
    if (sourceDrawer) this.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
    this.getSectionsToRender().forEach(section => {
      const html = parsedState.sections?.[section.id];
      if (!html) return;
      const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (target) target.innerHTML = this.getSectionInnerHTML(html, section.selector);
    });
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    const scroll = this.querySelector('.av-cart-scroll');
    if (scroll) scroll.scrollTop = scrollTop;
    if (this.classList.contains('active')) {
      const container = this.querySelector('.drawer__inner');
      const focus = focusedId ? document.getElementById(focusedId) : null;
      trapFocus(container, focus && this.contains(focus) ? focus : this.querySelector('.drawer__close'));
    } else if (options.open !== false) {
      this.open();
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  enableLoading(line) {
    super.enableLoading(line);
    const checkout = document.getElementById('CartDrawer-Checkout');
    if (checkout) checkout.disabled = true;
  }

  disableLoading(line) {
    super.disableLoading(line);
    const drawer = document.querySelector('cart-drawer');
    const checkout = document.getElementById('CartDrawer-Checkout');
    if (checkout && drawer) checkout.disabled = drawer.classList.contains('is-empty') || Boolean(drawer.dataset.avCartBusy);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Drawer-quantity-${id}`);
    if (input) input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  async onCartUpdate() {
    const drawer = this.closest('cart-drawer');
    if (!drawer || drawer.dataset.avCartBusy) return;
    try {
      const url = new URL(routes.cart_url, location.origin);
      url.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
      const response = await fetch(url, { signal: AbortSignal.timeout(15000) });
      if (!response.ok) return;
      const sections = await response.json();
      if (this.isConnected && !drawer.dataset.avCartBusy && !drawer.querySelector('.cart__items--disabled') && sections['cart-drawer']) drawer.renderContents({ sections }, { open: false });
    } catch (error) {
      const status = document.getElementById('CartDrawer-CartErrors');
      if (status) status.textContent = window.cartStrings.error;
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

